import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Package,
  Sparkles,
  Calendar,
  Layers,
  Boxes,
  Tag,
  BookOpen,
  Shirt,
  GlassWater,
  Scale,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile aux Loges-en-Josas ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Livraison de vos fournitures de déménagement directement
              chez vous {city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que transporter des paquets de cartons volumineux
              dans une voiture citadine est une contrainte évitable,
              notre service logistique de proximité prend tout en charge.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Dépose sur-mesure",
                body: "Nous acheminons l'intégralité de vos commandes à plat directement sur le pas de votre porte aux Loges-en-Josas, mais également dans les communes voisines comme Jouy-en-Josas, Buc, Toussus-le-Noble, Vélizy-Villacoublay et Versailles.",
                Icon: Truck,
              },
              {
                title: "Flexibilité totale",
                body: "Pas de journée bloquée à attendre le livreur. Nous fixons ensemble un créneau horaire précis selon vos disponibilités. Vos fournitures pro vous sont remises dans un état de propreté irréprochable, 100% sèches et prêtes à être montées immédiatement.",
                Icon: Calendar,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Formats d'emballage ciblés ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Formats ciblés
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Des formats d&apos;emballage ciblés pour sécuriser chaque
            pièce
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            On ne remplit pas un carton de vêtements de la même manière
            qu&apos;une collection de livres d&apos;art ou de la
            vaisselle fine. Notre boutique en ligne propose des solutions
            adaptées à chaque besoin.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              title: "La cartonnerie lourde et standard",
              body: "Nos petits modèles renforcés à double cannelure sont spécialement calibrés pour vos archives, documents denses et livres, empêchant le fond de céder au levage. Les cartons standards, quant à eux, accueillent idéalement le linge de maison ou le petit électroménager.",
              Icon: BookOpen,
            },
            {
              title: "Les protections spécifiques",
              body: "Pour vos dressings, nos cartons penderies verticaux intègrent une tringle métallique pour suspendre vos robes et costumes directement sur cintres. Pour la cuisine, nos cartons à croisillons isolent chaque verre et chaque assiette pour éliminer définitivement les risques de casse.",
              Icon: Shirt,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-les-loges-en-josas-cta-formats"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir tous les formats de cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/cartons-demenagement?categorie=speciaux"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-les-loges-en-josas-cta-speciaux"
          >
            <GlassWater className="h-4 w-4" strokeWidth={2.6} />
            Penderies &amp; croisillons
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Les 4 règles d'or ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Méthode pro
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Les 4 règles d&apos;or pour un emballage efficace et sans
            stress
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Gagnez du temps et sécurisez vos manipulations le jour J en
            adoptant les bons réflexes professionnels lors de la mise en
            boîte.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              n: "01",
              title: "Le calage des vides",
              body: "Utilisez notre film à bulles haute densité pour combler les espaces vides dans vos cartons. Un carton bien rempli ne doit émettre aucun bruit lorsqu'on le secoue légèrement.",
              Icon: Layers,
            },
            {
              n: "02",
              title: "L'équilibrage des masses",
              body: "Placez systématiquement les objets les plus denses et lourds au fond du carton, et terminez par les éléments légers sur le dessus pour éviter tout risque d'écrasement.",
              Icon: Scale,
            },
            {
              n: "03",
              title: "Le scellage en croix",
              body: "Appliquez votre ruban adhésif professionnel en croisant les bandes sur le fond du carton pour renforcer sa structure avant de le porter.",
              Icon: ShieldCheck,
            },
            {
              n: "04",
              title: "L'étiquetage stratégique",
              body: "Écrivez la pièce de destination sur les côtés du carton (et non sur le dessus) pour que l'information reste visible même lorsque les boîtes sont empilées dans le camion.",
              Icon: Tag,
            },
          ].map(({ n, title, body, Icon }) => (
            <div
              key={n}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5]">
                  Règle&nbsp;{n}
                </div>
                <h3 className="mt-1 font-display font-extrabold text-[16px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-les-loges-en-josas-cta-accessoires"
          >
            <Sparkles className="h-4 w-4" strokeWidth={2.6} />
            Adhésifs &amp; accessoires de fermeture
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-les-loges-en-josas-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
